import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  api,
  type Product,
  type StockMovement,
  type StockMovementType,
} from "../api";
import { fmtDateTime, fmtMoney, fmtQty } from "../lib/format";
import { useBranch } from "../lib/use-branch";
import { BranchLabel } from "./branch-label";
import { Button } from "./ui/button";
import { EmptyState } from "./ui/empty-state";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Money, Mono, StatusChip } from "./ui/ledger";
import { Pagination } from "./ui/pagination";
import { Select } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";

const PAGE_SIZE = 25;

/** Harakat kelib chiqqan hujjat — jurnaldan o'sha hujjatga o'tish uchun. */
export type MovementDocRef = {
  kind: "purchase" | "sale" | "return" | "work" | "writeoff";
  id: number;
};

/**
 * Satr qaysi hujjatdan kelganini aniqlaydi.
 *
 * Beshtasi ham `null` bo'lishi MUMKIN va bu xato emas: `tuzatish`
 * (inventarizatsiya) va bekor qilingan sotuvning kompensatsiyasi hech qanday
 * hujjatga bog'lanmaydi.
 */
function docRefOf(m: StockMovement): MovementDocRef | null {
  if (m.purchase_id != null) return { kind: "purchase", id: m.purchase_id };
  if (m.sale_id != null) return { kind: "sale", id: m.sale_id };
  if (m.return_id != null) return { kind: "return", id: m.return_id };
  if (m.work_id != null) return { kind: "work", id: m.work_id };
  if (m.write_off_id != null) return { kind: "writeoff", id: m.write_off_id };
  return null;
}

type Filters = {
  tur: "" | StockMovementType;
  branchId: string; // "" = barcha filiallar
  from: string;
  to: string;
};

const EMPTY_FILTERS: Filters = { tur: "", branchId: "", from: "", to: "" };

/**
 * Tovar harakatlari jurnali — analitika oynasining ikkinchi tabi.
 *
 * Analitikadan farqi: bu yerda AGREGAT emas, xom ledger. Har satr — bitta
 * yozuv: qachon, qaysi tur, qancha, qaysi filialda, qaysi hujjatdan va kim
 * yozgani. "Bu tovar qayerga ketdi?" savoliga faqat shu ko'rinish javob
 * beradi.
 *
 * Sahifalanadi (ledgerdagi "ko'proq yuklash" emas): faol tovarda minglab satr
 * bo'ladi, ularni to'plab borish xotirani ham, scrollni ham ma'nosiz
 * shishirardi.
 *
 * Tannarx ustuni ATAYIN yo'q — server uni xodimdan maskalaydi, va jurnalda
 * u baribir kerak emas: sotuv narxi va summasi yetarli.
 */
export function ProductMovementsTab({
  product,
  onOpenDoc,
}: {
  product: Product;
  onOpenDoc: (ref: MovementDocRef) => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  // Filial o'lchovi faqat bir nechta filialli ustaxonada: filtr ham, ustun ham.
  const { branches, showSelector } = useBranch();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<StockMovement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const query = useMemo(
    () => ({
      tur: filters.tur || undefined,
      branch_id: filters.branchId ? Number(filters.branchId) : undefined,
      date_from: filters.from || undefined,
      // Sana kiritish "kun" ma'nosida — kun oxirigacha qamraymiz (backend
      // `created_at <= date_to` deb solishtiradi), Qaytarishlar bilan bir xil.
      date_to: filters.to ? `${filters.to}T23:59:59` : undefined,
    }),
    [filters],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .productMovements(product.id, { ...query, page, page_size: PAGE_SIZE })
      .then((pg) => {
        if (!alive) return;
        setItems(pg.items);
        setTotal(pg.total);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [product.id, query, page, t]);

  /** Har qanday filtr o'zgarishi 1-sahifaga qaytaradi. */
  function patch(next: Partial<Filters>) {
    setFilters((f) => ({ ...f, ...next }));
    setPage(1);
  }

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="mv-tur">{t("tovars.movements.filters.tur")}</Label>
          <Select
            id="mv-tur"
            value={filters.tur}
            onChange={(e) =>
              patch({ tur: e.target.value as "" | StockMovementType })
            }
          >
            <option value="">{t("tovars.movements.filters.all")}</option>
            <option value="kirim">{t("tovars.analytics.movement.kirim")}</option>
            <option value="chiqim">{t("tovars.analytics.movement.chiqim")}</option>
            <option value="tuzatish">
              {t("tovars.analytics.movement.tuzatish")}
            </option>
          </Select>
        </div>
        {showSelector && (
          <div className="space-y-1.5">
            <Label htmlFor="mv-branch">
              {t("tovars.movements.filters.branch")}
            </Label>
            <Select
              id="mv-branch"
              value={filters.branchId}
              onChange={(e) => patch({ branchId: e.target.value })}
            >
              <option value="">{t("tovars.movements.filters.all")}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="mv-from">{t("tovars.movements.filters.from")}</Label>
          <Input
            id="mv-from"
            type="date"
            value={filters.from}
            onChange={(e) => patch({ from: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mv-to">{t("tovars.movements.filters.to")}</Label>
          <Input
            id="mv-to"
            type="date"
            value={filters.to}
            onChange={(e) => patch({ to: e.target.value })}
          />
        </div>
      </div>

      {hasFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setFilters(EMPTY_FILTERS);
            setPage(1);
          }}
        >
          {t("tovars.movements.filters.reset")}
        </Button>
      )}

      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2 text-sm">
          {err}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title={
            hasFilters
              ? t("tovars.movements.emptyFiltered")
              : t("tovars.analytics.noMovements")
          }
          className="py-8"
        />
      ) : (
        <div className="rounded-md border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("tovars.analytics.movement.date")}</TableHead>
                  <TableHead>{t("tovars.analytics.movement.tur")}</TableHead>
                  <TableHead className="text-right">
                    {t("tovars.analytics.movement.miqdor")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("tovars.analytics.movement.summa")}
                  </TableHead>
                  {showSelector && (
                    <TableHead>{t("shared.ledger.branch")}</TableHead>
                  )}
                  <TableHead>{t("tovars.movements.table.hujjat")}</TableHead>
                  <TableHead>{t("tovars.movements.table.xodim")}</TableHead>
                  <TableHead>{t("tovars.movements.table.izoh")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => {
                  const ref = docRefOf(m);
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="whitespace-nowrap">
                        <Mono className="text-muted-foreground">
                          {fmtDateTime(m.created_at)}
                        </Mono>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          tone={
                            m.tur === "kirim"
                              ? "ok"
                              : m.tur === "chiqim"
                                ? "warn"
                                : "muted"
                          }
                        >
                          {t(`tovars.analytics.movement.${m.tur}`)}
                        </StatusChip>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Money tone={m.tur === "chiqim" ? "neg" : "default"}>
                          {fmtQty(m.miqdor)} {product.birlik}
                        </Money>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Money>{fmtMoney(m.jami_summa)}</Money>
                      </TableCell>
                      {showSelector && (
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {/* Nom serverdan keladi (ledger bilan bir so'rovda),
                              shuning uchun arxivlangan filial ham to'g'ri
                              nomlanadi. */}
                          <BranchLabel
                            name={m.branch_name}
                            isActive={m.branch_is_active}
                          />
                        </TableCell>
                      )}
                      <TableCell className="whitespace-nowrap">
                        {ref ? (
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0"
                            onClick={() => onOpenDoc(ref)}
                          >
                            {t(`tovars.movements.doc.${ref.kind}`, { id: ref.id })}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {m.user_name || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[220px] truncate">
                        {m.izoh || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            total={total}
            pageSize={PAGE_SIZE}
            onPage={setPage}
          />
        </div>
      )}
    </div>
  );
}
